import { POIShape } from './POIShape';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { AppSettingsData } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';
import { Vector3 } from '../types';
import { BillboardUtils } from '../utils/BillboardUtils';
import { AssetLoadingBus } from '../core/AssetLoadingBus';
import { Scene } from '../core/Scene';

/**
 * GLTFShape loads and displays 3D models from GLTF/GLB files
 * Unlike other shapes, this doesn't create geometry but loads external assets
 */
export class GLTFShape extends POIShape {
    private loadedScene: THREE.Group | null = null;
    private loader: GLTFLoader;
    private animationMixer: THREE.AnimationMixer | null = null;
    private animationActions: THREE.AnimationAction[] = [];
    private isAnimationPlaying: boolean = false;

    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings);
        this.loader = new GLTFLoader();
        // Enable DRACO for compressed GLB/GLTF
        try {
            const dracoLoader = new DRACOLoader();
            // Use stable versioned CDN path; contains draco_decoder.wasm/js wrappers
            dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.6/');
            // Prefer WASM for performance and compatibility
            dracoLoader.setDecoderConfig({ type: 'wasm' });
            dracoLoader.preload();
            this.loader.setDRACOLoader(dracoLoader);
        } catch {}
        // Enable meshopt (EXT_meshopt_compression) — the storage ?preset=web pipeline
        // now emits gltfpack/meshopt GLBs (e.g. map 114637), and the phase-3 species
        // LOD ladder uses mesh_compression=meshopt as its mid tier.
        try { this.loader.setMeshoptDecoder(MeshoptDecoder); } catch {}
        
        ////Logger.Instance.Info(`🏗️ GLTFShape: Constructor called for POI ${poi.name}`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: URI: ${poi.poidata.uri?.value}`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: Scale from POI data: ${poi.poidata.scale.value}`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: Local offset from POI data: ${poi.poidata.localTranslationOffset.value?.toString() || 'none'}`, 'GLTFShape');
        
        // 🔍 DEBUG: Check if shape animations are properly inherited
        ////Logger.Instance.Info(`🎬 GLTFShape: shapeAnimator initialized: ${!!this.shapeAnimator}`, 'GLTFShape');
        ////Logger.Instance.Info(`🎬 GLTFShape: Animation properties - enableRotation: ${poi.poidata.enableRotation.value}, enableWiggle: ${poi.poidata.enableWiggle.value}, enableBillboard: ${poi.poidata.enableBillboard.value}`, 'GLTFShape');
        
        // Listen for local offset changes to update model position
        this.poi.poidata.localTranslationOffset.on(() => {
            if (this.loadedScene) {
                const localTranslationOffset = this.poi.poidata.localTranslationOffset.value ?? Vector3.zero();
                this.loadedScene.position.set(localTranslationOffset.x, localTranslationOffset.y, localTranslationOffset.z);
                ////Logger.Instance.Info(`🔧 GLTFShape: Updated local offset to: (${localTranslationOffset.x}, ${localTranslationOffset.y}, ${localTranslationOffset.z})`, 'GLTFShape');
            }
        });

                    // Listen for animation speed changes
            this.poi.poidata.glbAnimationSpeed.on(() => {
                if (this.animationMixer) {
                    const newSpeed = this.poi.poidata.glbAnimationSpeed.value ?? 1.0;
                    this.animationMixer.timeScale = newSpeed;
                    ////Logger.Instance.Info(`🎭 GLTFShape: Updated animation speed to ${newSpeed.toFixed(2)}x for POI ${this.poi.name}`, 'GLTFShape');
                }
            });
            
            // Listen for animation index changes to switch animations
            this.poi.poidata.glbAnimationIndex.on(() => {
                if (this.animationActions.length > 0 && this.isAnimationPlaying) {
                    const newIndex = this.poi.poidata.glbAnimationIndex.value ?? 0;
                    ////Logger.Instance.Info(`🎬 GLTFShape: Animation index changed to ${newIndex}, switching animation for POI ${this.poi.name}`, 'GLTFShape');
                    this.playAnimations(); // This will stop current and start new animation
                }
            });
        
        // Start loading after buildMesh() has completed
        setTimeout(() => {
            ////Logger.Instance.Info(`🏗️ GLTFShape: Starting GLB loading for POI ${poi.name}`, 'GLTFShape');
            ////Logger.Instance.Info(`🏗️ GLTFShape: _mesh available: ${!!this._mesh}`, 'GLTFShape');
            this.loadGLTFModel();
        }, 0);
    }

    protected override createGeometry(): THREE.BufferGeometry {
        // For GLTF shapes, we create a placeholder geometry that will be replaced
        // when the model loads. This ensures the POIShape system works correctly.
        return new THREE.BoxGeometry(0.1, 0.1, 0.1);
    }

    private async loadGLTFModel(): Promise<void> {
        const uris = this.poi.poidata.uri?.value || [];
        const uri = uris[0]; // Use first URI for GLTF model
        
        ////Logger.Instance.Info(`🏗️ GLTFShape: loadGLTFModel() called for POI ${this.poi.name}`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: URI value: "${uri}"`, 'GLTFShape');
        
        if (!uri) {
            Logger.Instance.Warn(`🏗️ GLTFShape: No model URL provided for POI ${this.poi.name}`, 'GLTFShape');
            return;
        }

        ////Logger.Instance.Info(`🏗️ GLTFShape: Starting GLTFLoader.load() for ${uri}`, 'GLTFShape');

        // Notify start for progress UI
        try { AssetLoadingBus.emit('asset:load-start', { type: 'gltf', uri, poi: this.poi }); } catch {}

        return new Promise((resolve, reject) => {
            this.loader.load(
                uri,
                (gltf) => {
                    ////Logger.Instance.Info(`🏗️ GLTFShape: GLTFLoader.load() SUCCESS for ${uri}`, 'GLTFShape');
                    this.onModelLoaded(gltf);
                    try { AssetLoadingBus.emit('asset:load-complete', { type: 'gltf', uri, poi: this.poi }); } catch {}
                    resolve();
                },
                (progress) => {
                    // Optional: Handle loading progress
                    const percentage = (progress.loaded / progress.total) * 100;
                    try { AssetLoadingBus.emit('asset:load-progress', { type: 'gltf', uri, poi: this.poi, progress: percentage }); } catch {}
                    ////Logger.Instance.Info(`🏗️ GLTFShape: Loading progress: ${percentage.toFixed(0)}% (${progress.loaded}/${progress.total} bytes)`, 'GLTFShape');
                },
                (error) => {
                    Logger.Instance.Error(`🏗️ GLTFShape: GLTFLoader.load() FAILED for ${uri}`, 'GLTFShape');
                    const errorObj = error as Error;
                    Logger.Instance.Error(`🏗️ GLTFShape: Error details: ${errorObj.message}`, 'GLTFShape');
                    this.onModelLoadError(error);
                    try { AssetLoadingBus.emit('asset:load-error', { type: 'gltf', uri, poi: this.poi, error: errorObj?.message || String(error) }); } catch {}
                    reject(error);
                }
            );
        });
    }

    private onModelLoaded(gltf: any): void {
        ////Logger.Instance.Info(`🏗️ GLTFShape: Successfully loaded GLTF model for POI ${this.poi.name}`, 'GLTFShape');
        
        this.loadedScene = gltf.scene;
        
        if (!this.loadedScene) {
            Logger.Instance.Error(`🏗️ GLTFShape: Invalid GLTF scene for POI ${this.poi.name}`, 'GLTFShape');
            return;
        }
        
        // 🔍 DEBUG + NAME REPAIR: Dump raw glTF JSON names and map back to Three objects
        try {
            const parser = (gltf as any)?.parser;
            const json = parser?.json;
            const associations = parser?.associations;
            if (associations && json && this.loadedScene) {
                this.loadedScene.traverse((obj: any) => {
                    try {
                        const assoc = associations.get(obj);
                        // Map node name → Object3D.name
                        if ((!obj.name || obj.name === '') && assoc && assoc.type === 'nodes') {
                            const src = json.nodes?.[assoc.index];
                            if (src?.name) obj.name = src.name;
                        }
                        if (obj.isMesh) {
                            const mesh = obj as THREE.Mesh;
                            const gAssoc = associations.get(mesh.geometry);
                            if (gAssoc && gAssoc.type === 'meshes') {
                                const ms = json.meshes?.[gAssoc.index];
                                if (ms?.name && (!mesh.geometry.name || mesh.geometry.name === '')) {
                                    mesh.geometry.name = ms.name;
                                }
                            }
                            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                            mats.forEach((m: any) => {
                                const mAssoc = associations.get(m);
                                if (mAssoc && mAssoc.type === 'materials') {
                                    const mm = json.materials?.[mAssoc.index];
                                    if (mm?.name && (!m.name || m.name === '')) m.name = mm.name;
                                }
                            });
                        }
                    } catch {}
                });
            }
        } catch {}

        // 🔍 Analyze the loaded model bounds and counts
        const modelBounds = new THREE.Box3().setFromObject(this.loadedScene);
        const modelSize = modelBounds.getSize(new THREE.Vector3());
        const modelCenter = modelBounds.getCenter(new THREE.Vector3());
        
        ////Logger.Instance.Info(`🏗️ GLTFShape: Model bounds - Size: (${modelSize.x.toFixed(2)}, ${modelSize.y.toFixed(2)}, ${modelSize.z.toFixed(2)})`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: Model bounds - Center: (${modelCenter.x.toFixed(2)}, ${modelCenter.y.toFixed(2)}, ${modelCenter.z.toFixed(2)})`, 'GLTFShape');
        
        // Count meshes and materials
        let meshCount = 0;
        let materialCount = 0;
        this.loadedScene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                meshCount++;
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        materialCount += child.material.length;
                    } else {
                        materialCount++;
                    }
                }
            }
        });
        
        ////Logger.Instance.Info(`🏗️ GLTFShape: Model contains ${meshCount} meshes and ${materialCount} materials`, 'GLTFShape');
        
        // Optionale Normierung: laengste Kante auf 1, Zentrum auf den Ursprung.
        // Damit wird poidata.scale zur echten Weltgroesse — Voraussetzung fuer
        // Layouts mit einheitlichen Zellen (Honeycomb-3D: Modelle kommen in
        // voellig verschiedenen Quell-Einheiten aus der Pipeline).
        // Apply scale from POI data
        const scale = this.poi.poidata.scale.value ?? 1;
        if (this.poi.poidata.gltfNormalize?.value) {
            // Bounds NUR ueber sichtbare Meshes (die Blender-Pipeline laesst
            // unsichtbare Hilfs-Meshes am Ursprung zurueck, die das Zentrum kippen —
            // gemessen: Modell 0..1.83 hoch, POI bei 0, Modell haengt im oberen Drittel).
            // Zentrieren, indem die Kinder um -center verschoben werden: die
            // loadedScene selbst bleibt am POI-Ursprung, so ueberlebt der Offset
            // jeden spaeteren Scale-Reset am Wurzelknoten.
            const vis = new THREE.Box3();
            const tmp = new THREE.Box3();
            this.loadedScene.traverse((c: any) => {
                if (c.isMesh && c.visible && c.geometry) { tmp.setFromObject(c); if (!tmp.isEmpty()) vis.union(tmp); }
            });
            const box = vis.isEmpty() ? modelBounds : vis;
            const size = box.getSize(new THREE.Vector3());
            const center = box.getCenter(new THREE.Vector3());
            const maxDim = Math.max(size.x, size.y, size.z) || 1;
            const k = scale / maxDim;
            for (const child of this.loadedScene.children) child.position.sub(center);
            this.loadedScene.scale.set(k, k, k);
            this.loadedScene.position.set(0, 0, 0);
        } else {
            this.loadedScene.scale.set(scale, scale, scale);
        }
        ////Logger.Instance.Info(`🏗️ GLTFShape: Applied scale: ${scale} (from markdown)`, 'GLTFShape');
        
        // Apply local offset from POI data to center displaced models
        const localTranslationOffset = this.poi.poidata.localTranslationOffset.value;
        if (localTranslationOffset && !localTranslationOffset.equals(Vector3.zero())) {
            this.loadedScene.position.set(localTranslationOffset.x, localTranslationOffset.y, localTranslationOffset.z);
            ////Logger.Instance.Info(`🏗️ GLTFShape: Applied local offset: (${localTranslationOffset.x}, ${localTranslationOffset.y}, ${localTranslationOffset.z}) (from markdown)`, 'GLTFShape');
        }
        
        // Calculate final bounds after scaling
        const scaledBounds = new THREE.Box3().setFromObject(this.loadedScene);
        const scaledSize = scaledBounds.getSize(new THREE.Vector3());
        ////Logger.Instance.Info(`🏗️ GLTFShape: Final scaled size: (${scaledSize.x.toFixed(2)}, ${scaledSize.y.toFixed(2)}, ${scaledSize.z.toFixed(2)})`, 'GLTFShape');
        
        // Enable shadows if supported
        this.loadedScene.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.castShadow = true;
                child.receiveShadow = true;
                
                // 🔍 DEBUG: Log mesh details
                //Logger.Instance.Debug(`🏗️ GLTFShape: Mesh "${child.name}" - Vertices: ${child.geometry?.attributes?.position?.count || 'unknown'}`, 'GLTFShape');
                
                // Apply any material modifications if needed
                if (child.material) {
                    this.applyMaterialSettings(child.material);
                }
            }
        });

        // 🎭 SETUP: GLB animations if available
        this.setupAnimations(gltf);

        // 🎯 CORRECT: Replace the placeholder mesh with GLB model
        if (this._mesh && this.loadedScene) {
            ////Logger.Instance.Info(`🏗️ GLTFShape: Replacing placeholder mesh with GLB model`, 'GLTFShape');
            
            // 🎯 CRITICAL: Transfer any children (like labels) before replacing mesh
            const parent = this._mesh.parent;
            const children = [...this._mesh.children]; // Copy children array
            
            ////Logger.Instance.Info(`🏗️ GLTFShape: Found ${children.length} children on placeholder mesh`, 'GLTFShape');
            
            // Remove placeholder mesh from its parent
            if (parent) {
                parent.remove(this._mesh);
                ////Logger.Instance.Info(`🏗️ GLTFShape: Removed placeholder mesh from parent`, 'GLTFShape');
            }
            
            // Replace our internal _mesh reference with the loaded scene
            this._mesh = this.loadedScene as any;
            if (this._mesh) {
                this._mesh.userData.sceneNode = this;
            }
            
            // 🎯 TRANSFER: Move all children (labels, debug meshes) to new mesh
            children.forEach((child, index) => {
                if (this._mesh && this.loadedScene) {
                    this.loadedScene.add(child);
                    ////Logger.Instance.Info(`🏗️ GLTFShape: Transferred child ${index}: ${child.name || child.constructor.name}`, 'GLTFShape');
                }
            });
            
            // Add the new mesh to the parent
            if (parent && this.loadedScene) {
                parent.add(this.loadedScene);
                ////Logger.Instance.Info(`🏗️ GLTFShape: Added GLB model to parent`, 'GLTFShape');
            }
            
            // 🎯 CRITICAL FIX: Ensure GLB model has correct initial transform
            this.setupGLBTransform();
            
            // 🔍 DEBUG: Check bounds before creating debug meshes
            this.debugBoundsCalculation();
            
            // 🎯 CREATE: Standard debug mesh creation (no special geometry needed)
            this.finalizeMeshSetup();
            
            // Force transform update to ensure proper positioning
            const currentPos = this.transform.position.value;
            if (currentPos) {
                this.transform.position.value = currentPos;
            }
            
            ////Logger.Instance.Info(`🏗️ GLTFShape: ✅ GLB model successfully integrated for POI ${this.poi.name}`, 'GLTFShape');
        } else {
            Logger.Instance.Error(`🏗️ GLTFShape: ❌ No _mesh found for POI ${this.poi.name}`, 'GLTFShape');
        }
    }

    private onModelLoadError(error: any): void {
        Logger.Instance.Error(`🏗️ GLTFShape: Model loading failed for POI ${this.poi.name}: ${error.message}`, 'GLTFShape');
        
        // The placeholder mesh will remain visible as fallback
        ////Logger.Instance.Info(`🏗️ GLTFShape: Using placeholder mesh as fallback for POI ${this.poi.name}`, 'GLTFShape');
    }



    private applyMaterialSettings(material: THREE.Material | THREE.Material[]): void {
        const materials = Array.isArray(material) ? material : [material];
        
        materials.forEach(mat => {
            if (mat instanceof THREE.MeshStandardMaterial || mat instanceof THREE.MeshPhysicalMaterial) {
                // Apply global material settings if available
                if (this.sceneSettings) {
                    // Apply roughness/metalness from scene settings if desired
                    // mat.roughness = this.sceneSettings.defaultRoughness?.value || mat.roughness;
                    // mat.metalness = this.sceneSettings.defaultMetalness?.value || mat.metalness;
                }
            }
        });
    }

    /**
     * Get the loaded GLTF scene for direct manipulation if needed
     */
    public getLoadedScene(): THREE.Group | null {
        return this.loadedScene;
    }

    /**
     * Check if the model is fully loaded
     */
    public isModelLoaded(): boolean {
        return this.loadedScene !== null;
    }
    
    /**
     * 🎯 CRITICAL FIX: Setup GLB transform correctly
     * The debug meshes use setFromObject(this._mesh) so the GLB model itself needs correct bounds
     */
    private setupGLBTransform(): void {
        if (!this.loadedScene) return;
        
        ////Logger.Instance.Info(`🏗️ GLTFShape: Setting up GLB transform for correct bounds`, 'GLTFShape');
        
        // 🔍 BEFORE: Log current GLB bounds  
        const beforeBounds = new THREE.Box3().setFromObject(this.loadedScene);
        const beforeSize = beforeBounds.getSize(new THREE.Vector3());
        const beforeCenter = beforeBounds.getCenter(new THREE.Vector3());
        ////Logger.Instance.Info(`🏗️ GLTFShape: BEFORE transform - Size: (${beforeSize.x.toFixed(2)}, ${beforeSize.y.toFixed(2)}, ${beforeSize.z.toFixed(2)})`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: BEFORE transform - Center: (${beforeCenter.x.toFixed(2)}, ${beforeCenter.y.toFixed(2)}, ${beforeCenter.z.toFixed(2)})`, 'GLTFShape');
        
        // 🎯 RESET: Apply local offset if set, otherwise center at origin
        const localTranslationOffset = this.poi.poidata.localTranslationOffset.value ?? Vector3.zero();
        this.loadedScene.position.set(localTranslationOffset.x, localTranslationOffset.y, localTranslationOffset.z);
        this.loadedScene.rotation.set(0, 0, 0);
        
        if (!localTranslationOffset.equals(Vector3.zero())) {
            ////Logger.Instance.Info(`🔧 GLTFShape: Applied local offset in setupGLBTransform: (${localTranslationOffset.x}, ${localTranslationOffset.y}, ${localTranslationOffset.z})`, 'GLTFShape');
        }
        this.loadedScene.updateMatrix();
        this.loadedScene.updateMatrixWorld(true);
        
        // 🔍 AFTER: Log new GLB bounds
        const afterBounds = new THREE.Box3().setFromObject(this.loadedScene);
        const afterSize = afterBounds.getSize(new THREE.Vector3());
        const afterCenter = afterBounds.getCenter(new THREE.Vector3());
        ////Logger.Instance.Info(`🏗️ GLTFShape: AFTER transform - Size: (${afterSize.x.toFixed(2)}, ${afterSize.y.toFixed(2)}, ${afterSize.z.toFixed(2)})`, 'GLTFShape');
        ////Logger.Instance.Info(`🏗️ GLTFShape: AFTER transform - Center: (${afterCenter.x.toFixed(2)}, ${afterCenter.y.toFixed(2)}, ${afterCenter.z.toFixed(2)})`, 'GLTFShape');
        
        // 🔍 DEBUG: Check what _mesh bounds will be
        if (this._mesh) {
            const meshBounds = new THREE.Box3().setFromObject(this._mesh);
            const meshSize = meshBounds.getSize(new THREE.Vector3());
            ////Logger.Instance.Info(`🏗️ GLTFShape: _mesh bounds - Size: (${meshSize.x.toFixed(2)}, ${meshSize.y.toFixed(2)}, ${meshSize.z.toFixed(2)})`, 'GLTFShape');
        }
    }

    /**
     * 🎭 SETUP: GLB animations using Three.js AnimationMixer
     */
    private setupAnimations(gltf: any): void {
        if (!gltf.animations || gltf.animations.length === 0) {
            ////Logger.Instance.Info(`🎭 GLTFShape: No animations found in GLB for POI ${this.poi.name}`, 'GLTFShape');
            return;
        }

        ////Logger.Instance.Info(`🎭 GLTFShape: Found ${gltf.animations.length} animations in GLB for POI ${this.poi.name}`, 'GLTFShape');

        // Create AnimationMixer
        this.animationMixer = new THREE.AnimationMixer(this.loadedScene!);
        this.animationActions = [];

        // Create animation actions for all animations
        gltf.animations.forEach((clip: THREE.AnimationClip, index: number) => {
            const action = this.animationMixer!.clipAction(clip);
            
            // 🎯 FIX: Proper animation setup like Sketchfab
            action.setLoop(THREE.LoopRepeat, Infinity);
            action.clampWhenFinished = false; // Allow smooth looping
            action.reset(); // Start from beginning
            action.setEffectiveWeight(1.0); // Full weight
            action.setEffectiveTimeScale(1.0); // Normal speed initially
            
            this.animationActions.push(action);
            
            ////Logger.Instance.Info(`🎭 GLTFShape: Created animation action ${index}: "${clip.name}" (duration: ${clip.duration.toFixed(2)}s)`, 'GLTFShape');
            ////Logger.Instance.Info(`🎭 GLTFShape: Action weight: ${action.getEffectiveWeight()}, timeScale: ${action.getEffectiveTimeScale()}`, 'GLTFShape');
            
            // 🔍 DEBUG: Analyze animation tracks for range debugging
            this.debugAnimationTracks(clip, index);
        });
        
        // 🎯 ANIMATIONS: Log all available animations for user selection
        ////Logger.Instance.Info(`🎬 GLTFShape: === Available Animations (${gltf.animations.length}) ===`, 'GLTFShape');
        gltf.animations.forEach((clip: THREE.AnimationClip, index: number) => {
            ////Logger.Instance.Info(`🎬 GLTFShape:   [${index}] "${clip.name}" (${clip.duration.toFixed(2)}s)`, 'GLTFShape');
        });
        ////Logger.Instance.Info(`🎬 GLTFShape: Current selection: Animation ${this.poi.poidata.glbAnimationIndex.value} (change via glbAnimationIndex in markdown)`, 'GLTFShape');

        // 🎯 SPEED: Set animation playback speed
        const animationSpeed = this.poi.poidata.glbAnimationSpeed.value ?? 1.0;
        this.animationMixer.timeScale = animationSpeed;
        ////Logger.Instance.Info(`🎭 GLTFShape: Set animation speed to ${animationSpeed.toFixed(2)}x for POI ${this.poi.name}`, 'GLTFShape');

        // 🎯 CONTROL: Start/stop animations based on POI active state and setting
        const shouldPlay = this.poi.poidata.glbAnimationOnActive.value && this.poi.isActive();
        if (shouldPlay) {
            this.playAnimations();
        } else {
            ////Logger.Instance.Info(`🎭 GLTFShape: Animations prepared but not started (onActive: ${this.poi.poidata.glbAnimationOnActive.value}, POI active: ${this.poi.isActive()})`, 'GLTFShape');
            
            // 🎯 TEMP DEBUG: Force start animations to test if Three.js setup works (remove this later)
            ////Logger.Instance.Info(`🎯 GLTFShape: TEMP DEBUG - Force starting animations to test Three.js implementation`, 'GLTFShape');
            this.playAnimations();
        }
    }

    /**
     * 🎮 CONTROL: Play all GLB animations
     */
    private playAnimations(): void {
        if (!this.animationMixer || this.animationActions.length === 0) {
            Logger.Instance.Warn(`⚠️ GLTFShape: Cannot play animations - mixer: ${!!this.animationMixer}, actions: ${this.animationActions.length}`, 'GLTFShape');
            return;
        }

        // 🎯 FIX: Play only ONE selected animation (like Sketchfab), not all at once
        const selectedIndex = this.poi.poidata.glbAnimationIndex.value ?? 0;
        const clampedIndex = Math.max(0, Math.min(selectedIndex, this.animationActions.length - 1));
        
        if (selectedIndex !== clampedIndex) {
            Logger.Instance.Warn(`⚠️ GLTFShape: Animation index ${selectedIndex} out of range, using ${clampedIndex} instead`, 'GLTFShape');
        }

        // Stop all animations first
        this.animationActions.forEach((action, index) => {
            action.stop();
            action.setEffectiveWeight(0.0);
        });

        // Start only the selected animation
        const selectedAction = this.animationActions[clampedIndex];
        if (selectedAction) {
            selectedAction.reset();
            selectedAction.setEffectiveWeight(1.0);
            selectedAction.setEffectiveTimeScale(1.0);
            selectedAction.enabled = true;
            selectedAction.paused = false;
            selectedAction.play();
            
            const clip = selectedAction.getClip();
            ////Logger.Instance.Info(`🎮 GLTFShape: Started SELECTED animation ${clampedIndex} "${clip.name}" (${clip.duration.toFixed(2)}s) for POI ${this.poi.name}`, 'GLTFShape');
            ////Logger.Instance.Info(`🎮 GLTFShape:   Weight: ${selectedAction.getEffectiveWeight()}, TimeScale: ${selectedAction.getEffectiveTimeScale()}, Enabled: ${selectedAction.enabled}`, 'GLTFShape');
        }

        this.isAnimationPlaying = true;
        ////Logger.Instance.Info(`🎮 GLTFShape: Playing animation ${clampedIndex}/${this.animationActions.length - 1} for POI ${this.poi.name}`, 'GLTFShape');
    }

    /**
     * ⏸️ CONTROL: Pause all GLB animations
     */
    private pauseAnimations(): void {
        if (!this.animationMixer || this.animationActions.length === 0) return;

        this.animationActions.forEach((action, index) => {
            action.paused = true;
            ////Logger.Instance.Info(`⏸️ GLTFShape: Paused animation ${index} for POI ${this.poi.name}`, 'GLTFShape');
        });

        this.isAnimationPlaying = false;
        ////Logger.Instance.Info(`⏸️ GLTFShape: All animations paused for POI ${this.poi.name}`, 'GLTFShape');
    }

    /**
     * 🎯 ACTIVATION: Called when POI becomes active
     */
    public override setActive(): void {
        if (this.poi.poidata.glbAnimationOnActive.value) {
            ////Logger.Instance.Info(`🎯 GLTFShape: POI ${this.poi.name} became active, starting animations`, 'GLTFShape');
            this.playAnimations();
        } else {
            ////Logger.Instance.Info(`🎯 GLTFShape: POI ${this.poi.name} became active, but glbAnimationOnActive is disabled`, 'GLTFShape');
        }
    }

    /**
     * 🔄 DEACTIVATION: Called when POI becomes inactive
     */
    public override setInactive(): void {
        if (this.isAnimationPlaying) {
            ////Logger.Instance.Info(`🔄 GLTFShape: POI ${this.poi.name} became inactive, pausing animations`, 'GLTFShape');
            this.pauseAnimations();
        } else {
            ////Logger.Instance.Info(`🔄 GLTFShape: POI ${this.poi.name} became inactive, animations were already paused`, 'GLTFShape');
        }
    }

    /**
     * 🔄 UPDATE: Called every frame to update animations
     */
    protected override onUpdate(deltaTime: number): void {
        // Update AnimationMixer if animations are playing
        if (this.animationMixer && this.isAnimationPlaying) {
            // 🔍 DEBUG: Log animation update details periodically
            const currentTime = this.animationMixer.time;
            if (Math.floor(currentTime * 10) % 10 === 0) { // Log every 0.1 seconds
                //Logger.Instance.Debug(`🎬 GLTFShape: Animation update - deltaTime: ${deltaTime.toFixed(4)}s, mixerTime: ${currentTime.toFixed(2)}s`, 'GLTFShape');
                
                // Check if any animations are actually running
                this.animationActions.forEach((action, index) => {
                    const clip = action.getClip();
                    const actionTime = action.time;
                    const progress = (actionTime / clip.duration * 100).toFixed(1);
                    //Logger.Instance.Debug(`🎬 GLTFShape:   Action ${index}: time=${actionTime.toFixed(2)}s/${clip.duration.toFixed(2)}s (${progress}%)`, 'GLTFShape');
                });
            }
            
            this.animationMixer.update(deltaTime);
        }

        // 🔍 DEBUG: Check shape animations before calling parent
        if (this.shapeAnimator) {
            const enableRotation = this.poi.poidata.enableRotation.value ?? false;
            const enableWiggle = this.poi.poidata.enableWiggle.value ?? false;
            const enableBillboard = this.poi.poidata.enableBillboard.value ?? false;
            
            if (enableRotation || enableWiggle || enableBillboard) {
                //Logger.Instance.Debug(`🎬 GLTFShape: Shape animations active - rotation: ${enableRotation}, wiggle: ${enableWiggle}, billboard: ${enableBillboard}`, 'GLTFShape');
            }
        } else {
            Logger.Instance.Warn(`⚠️ GLTFShape: No shapeAnimator found for POI ${this.poi.name}!`, 'GLTFShape');
        }

        // Call parent update (this should handle shape animations via POIShape.onUpdate)
        super.onUpdate(deltaTime);

        // Extra: Apply billboard on the loaded GLB root if enabled for map anchors
        try {
            const appSettings = this.poi.getAppSettings();
            const anchorBillboard = appSettings?.enableBillboardForMapAnchors?.value ?? false;
            if (anchorBillboard && this.loadedScene) {
                const yawOnly = appSettings?.shapeBillboardYawOnly?.value ?? false;
                if (yawOnly) {
                    // Y-only (yaw) billboard
                    const objPos = this.transform.getWorldPosition().toThree();
                    const camera = Scene.Instance.getCamera();
                    if (camera) {
                        const camPos = camera.position.clone();
                        camPos.y = objPos.y;
                        const look = new THREE.Matrix4();
                        look.lookAt(objPos, camPos, new THREE.Vector3(0,1,0));
                        const q = new THREE.Quaternion().setFromRotationMatrix(look);
                        this.loadedScene.quaternion.copy(q);
                    }
                } else {
                    // Full billboard: reuse Transform-based util and copy to GLB root
                    const q = BillboardUtils.calculateBillboardRotation(this.transform);
                    if (q) this.loadedScene.quaternion.copy(q.toThree());
                }
            }
        } catch {}
    }

    /**
     * 🗑️ CLEANUP: Dispose animations when shape is destroyed
     */
    public override dispose(): void {
        // Stop and dispose animations
        if (this.animationMixer) {
            this.animationMixer.stopAllAction();
            this.animationActions = [];
            this.animationMixer = null;
            this.isAnimationPlaying = false;
            ////Logger.Instance.Info(`🗑️ GLTFShape: Disposed animations for POI ${this.poi.name}`, 'GLTFShape');
        }

        // Clean up loaded resources
        if (this.loadedScene) {
            this.loadedScene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(mat => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
        }
        
        super.dispose();
    }
    
    /**
     * 🔍 DEBUG: Analyze animation tracks for export debugging
     */
    private debugAnimationTracks(clip: THREE.AnimationClip, clipIndex: number): void {
        ////Logger.Instance.Info(`🎭 GLTFShape: === Animation ${clipIndex} Analysis ===`, 'GLTFShape');
        ////Logger.Instance.Info(`🎭 GLTFShape: Name: "${clip.name}"`, 'GLTFShape');
        ////Logger.Instance.Info(`🎭 GLTFShape: Duration: ${clip.duration.toFixed(3)}s`, 'GLTFShape');
        ////Logger.Instance.Info(`🎭 GLTFShape: Tracks: ${clip.tracks.length}`, 'GLTFShape');
        
        clip.tracks.forEach((track, trackIndex) => {
            const keyframeCount = track.times.length;
            const firstTime = track.times[0]?.toFixed(3) || 'none';
            const lastTime = track.times[keyframeCount - 1]?.toFixed(3) || 'none';
            const trackType = track.name.split('.').pop(); // position, rotation, scale
            
            ////Logger.Instance.Info(`🎭 GLTFShape:   Track ${trackIndex}: ${track.name} (${trackType})`, 'GLTFShape');
            ////Logger.Instance.Info(`🎭 GLTFShape:     Keyframes: ${keyframeCount}, Range: ${firstTime}s - ${lastTime}s`, 'GLTFShape');
            
            // Check for potential range issues
            if (parseFloat(firstTime) > 0.1) {
                Logger.Instance.Warn(`⚠️ GLTFShape:     WARNING: Animation doesn't start at 0! First keyframe at ${firstTime}s`, 'GLTFShape');
            }
            
            if (keyframeCount < 2) {
                Logger.Instance.Warn(`⚠️ GLTFShape:     WARNING: Only ${keyframeCount} keyframe(s) - animation may not work properly`, 'GLTFShape');
            }
        });
        
        ////Logger.Instance.Info(`🎭 GLTFShape: === End Analysis ===`, 'GLTFShape');
    }

    /**
     * 🔍 DEBUG: Check bounds calculation like Mesh3D does
     */
    private debugBoundsCalculation(): void {
        if (!this._mesh) return;
        
        ////Logger.Instance.Info(`🔍 GLTFShape: DEBUG bounds calculation for ${this.poi.name}`, 'GLTFShape');
        
        // 🔍 Check bounds WITH children (labels, etc)
        const boundsWithChildren = new THREE.Box3().setFromObject(this._mesh);
        const sizeWith = boundsWithChildren.getSize(new THREE.Vector3());
        ////Logger.Instance.Info(`🔍 GLTFShape: Bounds WITH children: (${sizeWith.x.toFixed(2)}, ${sizeWith.y.toFixed(2)}, ${sizeWith.z.toFixed(2)})`, 'GLTFShape');
        
        // 🔍 Check bounds WITHOUT children (like Mesh3D debug calculation)
        const children = [...this._mesh.children];
        this._mesh.children.length = 0;
        
        const boundsWithoutChildren = new THREE.Box3().setFromObject(this._mesh);
        const sizeWithout = boundsWithoutChildren.getSize(new THREE.Vector3());
        const centerWithout = boundsWithoutChildren.getCenter(new THREE.Vector3());
        
        ////Logger.Instance.Info(`🔍 GLTFShape: Bounds WITHOUT children: (${sizeWithout.x.toFixed(2)}, ${sizeWithout.y.toFixed(2)}, ${sizeWithout.z.toFixed(2)})`, 'GLTFShape');
        ////Logger.Instance.Info(`🔍 GLTFShape: Center WITHOUT children: (${centerWithout.x.toFixed(2)}, ${centerWithout.y.toFixed(2)}, ${centerWithout.z.toFixed(2)})`, 'GLTFShape');
        
        // Restore children
        this._mesh.children.push(...children);
        
        // 🔍 Check GLB internal structure
        if (this.loadedScene) {
            this.loadedScene.traverse((child) => {
                if (child instanceof THREE.Mesh) {
                    const childBounds = new THREE.Box3().setFromObject(child);
                    const childSize = childBounds.getSize(new THREE.Vector3());
                    ////Logger.Instance.Info(`🔍 GLTFShape: Child mesh "${child.name}" bounds: (${childSize.x.toFixed(2)}, ${childSize.y.toFixed(2)}, ${childSize.z.toFixed(2)})`, 'GLTFShape');
                    ////Logger.Instance.Info(`🔍 GLTFShape: Child mesh "${child.name}" position: (${child.position.x.toFixed(2)}, ${child.position.y.toFixed(2)}, ${child.position.z.toFixed(2)})`, 'GLTFShape');
                    ////Logger.Instance.Info(`🔍 GLTFShape: Child mesh "${child.name}" scale: (${child.scale.x.toFixed(2)}, ${child.scale.y.toFixed(2)}, ${child.scale.z.toFixed(2)})`, 'GLTFShape');
                }
            });
        }
    }

    /**
     * 🔍 DEBUG: Get debug information about the current state
     */
    public getDebugInfo(): any {
        return {
            modelLoaded: this.isModelLoaded(),
            meshExists: !!this._mesh,
            meshVisible: this._mesh?.visible,
            modelVisible: this.loadedScene?.visible,
            meshPosition: this._mesh?.position?.toArray(),
            modelPosition: this.loadedScene?.position?.toArray(),
            modelScale: this.loadedScene?.scale?.toArray(),
            uri: this.poi.poidata.uri?.value,
            hasGeometry: !!this._geometry,
            geometryVertices: this._geometry?.getAttribute('position')?.count || 0
        };
    }
}